import React from "react";
import {
  StyleImageWrapper,
  StyleListItem,
  StyleListWrapper,
  StyleOverviewCardChildren,
  StyleOverviewCardHeader,
  StyleOverviewCardWrapper,
  StyleStatus,
} from "./style";
import Typography from "@components/typography";
import theme from "@utils/theme";
import Row from "@components/row";
import useConfig from "@hooks/useConfig";
import { useNavigate, useSearchParams } from "react-router-dom";

const getInitials = (name?: string) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1 && words[0].length >= 2) {
    return words[0].substring(0, 2).toUpperCase();
  } else if (words.length === 1) {
    return words[0][0].toUpperCase();
  }
  return "";
};

const OverviewCard = ({
  children,
  title,
  link,
  data,
  type,
  showViewAll = true,
}: {
  children?: React.ReactNode;
  title?: string;
  link?: string;
  data?: any;
  type?: "product" | "deliveryman" | "customer";
  showViewAll?: boolean;
}) => {
  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleQuery = searchParams.get("module") || "food";

  const handleItemClick = (item: any) => {
    if (type === "product" && item.productId) {
      navigate(
        `/admin/users/store/update-product/${item.productId}?module=${moduleQuery}`,
        { state: { productData: { id: item.productId } } },
      );
    } else if (type === "deliveryman" && item.deliveryId) {
      navigate(
        `/admin/users/delivery-man/delivery-man-lists/view/${item.deliveryId}`,
        { state: { id: item.deliveryId, name: item.name || "Deliveryman" } },
      );
    } else if (type === "customer" && item.customerId) {
      navigate(`/admin/users/customer/customer-lists/view/${item.customerId}`, {
        state: { id: item.customerId, name: item.name || "Customer" },
      });
    }
  };

  return (
    <StyleOverviewCardWrapper>
      <StyleOverviewCardHeader>
        <Typography
          size={16}
          weight={600}
          styleText={`color:${theme.orange.orange800}`}
        >
          {title}
        </Typography>
        {showViewAll && (
          <p
            onClick={() => {
              navigate(link);
            }}
            style={{ cursor: "pointer" }}
          >
            {children ? "" : "View All"}
          </p>
        )}
      </StyleOverviewCardHeader>
      <StyleOverviewCardChildren>
        {!!children ? (
          children
        ) : (
          <>
            {(data && Array.isArray(data) ? data : []).map(
              (item: any, i: number) => (
                <StyleListWrapper key={i}>
                  <StyleListItem>
                    <div
                      onClick={() => handleItemClick(item)}
                      style={{ cursor: "pointer", width: "100%" }}
                    >
                      <Row styleRow="align-items:center;gap:10px">
                        <StyleImageWrapper>
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "relative",
                              borderRadius: "5px",
                              overflow: "hidden",
                              backgroundColor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid #ebebeb",
                              fontSize: "13px",
                              fontWeight: "bold",
                              color: "#666",
                            }}
                          >
                            {getInitials(item.name || "Unknown")}
                            {item.avatar && (
                              <img
                                src={
                                  item.avatar.startsWith("http")
                                    ? item.avatar
                                    : `${bucketUrl}/${item.avatar}`
                                }
                                alt={item.name}
                                onError={(e: any) =>
                                  (e.target.style.display = "none")
                                }
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </div>
                        </StyleImageWrapper>
                        <Typography
                          weight={600}
                          styleText={`color:black`}
                          size={14}
                        >
                          {item.name || "Unknown"}
                        </Typography>
                      </Row>
                    </div>
                    <StyleStatus>
                      {item.sold !== undefined
                        ? `Sold : ${item.sold}`
                        : `Orders : ${item.orderCount || 0}`}
                    </StyleStatus>
                  </StyleListItem>
                </StyleListWrapper>
              ),
            )}
            {(!data || data.length === 0) && (
              <Typography
                size={14}
                styleText="text-align: center; color: #888; padding: 20px 0;"
              >
                No items found
              </Typography>
            )}
          </>
        )}
      </StyleOverviewCardChildren>
    </StyleOverviewCardWrapper>
  );
};

export default OverviewCard;
